import { intl } from '@fullstory/intl';
import {
  SetRetentionMetricRequest,
  Space,
  UnnamedRetentionMetric,
} from '@fullstory/proto/fs/services/dx-objects';
import { useFlags, useNotifications } from '@fullstory/react-hooks';
import { generateRoutePath, RETENTION_DETAILS_ROUTE } from '@fullstory/routes';
import { hashObject } from '@fullstory/util/hash';
import { omit } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../types';
import { makeUseAccess, makeUseCreateAccess, stringIDs } from '../../utils/accessControl';
import { useRequestState } from '../../utils/hooks';
import { getOrgId } from '../fullstory/selectors';
import { RetentionMetricsRequestActions } from './actions';
import {
  getAllRetentionMetricsData,
  getRetentionMetricsAccess,
  getSavedRetentionMetricsById,
  getUnnamedRetentionMetrics,
} from './selectors';
import { filterOverlays } from '../overlays/utils';
import { RetentionMetricsById } from './types';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { SegmentRequestActions } from '../segments';
import { Visibility } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';

export const useAllRetentionMetrics = (includeOverlayedObjects: boolean = false) => {
  const orgId = useSelector(getOrgId);
  const flags = useFlags();
  const dispatch = useDispatch<AppDispatch>();
  const requestStateStatus = useRequestState(getAllRetentionMetricsData);

  const fetchRetentionMetrics = useCallback(() => {
    if (flags.hasBit(Flag.FeatRetention)) {
      dispatch(RetentionMetricsRequestActions.getAllRetentionMetrics(orgId)).catch(() =>
        console.error('Failed to get Retention charts'),
      );
    }
  }, [dispatch, flags, orgId]);

  useEffect(() => fetchRetentionMetrics(), [fetchRetentionMetrics]);

  const byId = useSelector(getSavedRetentionMetricsById);

  const filtered: RetentionMetricsById = useMemo(
    () => (includeOverlayedObjects ? byId : filterOverlays(byId)),
    [includeOverlayedObjects, byId],
  );
  return {
    ...(flags.hasBit(Flag.FeatRetention)
      ? requestStateStatus
      : { isLoading: false, isReady: true, hasError: false, data: null }),
    byId: filtered,
    savedById: filtered,
    fetchRetentionMetrics,
  };
};

export const useRetentionMetric = (retentionMetricId: string | undefined) => {
  const allRetentionMetrics = useAllRetentionMetrics();
  // overwrite data with the retention metric queried for
  const data = retentionMetricId ? allRetentionMetrics.byId[retentionMetricId] : null;
  return omit({ ...allRetentionMetrics, data }, ['byId', 'savedById']);
};

const emptyUnnamed = {
  isLoading: false,
  isReady: true,
  hasError: false,
  data: null,
};

export const useUnnamedRetentionMetric = (hash: string | undefined) => {
  const dispatch = useDispatch<AppDispatch>();
  const requestState = useRequestState(getUnnamedRetentionMetrics);
  // the request state acts across all requests to get unnamed retention metrics,
  // but the requests are fired by id - this is problematic particularly in the 404 scenario.
  // to prevent an infinite loop of error requests on the same hash,
  // localize an error state by hash (as opposed to globally on the request state).
  // this also allows refetching when the hash changes.
  const [hashError, setHashError] = useState<string | undefined>();

  const data = requestState.data?.[hash ?? ''];

  // reset error each time the hash changes
  useEffect(() => {
    setHashError(undefined);
  }, [hash]);

  useEffect(() => {
    // because we optimistically update on unnamed retention metrics,
    // no need to re-fetch if it already exists in state
    if (hash && !data && !hashError) {
      // hold on to hash value at request time to compare if we get an error later.
      const requestHash = hash;

      dispatch(RetentionMetricsRequestActions.getUnnamedRetentionMetric({ hash })).catch(() => {
        // only set the hash error if it still is equal to the hash value passed into the hook
        if (requestHash === hash) setHashError(hash);
        console.error('Failed to get temporary retention state');
      });
    }
  }, [dispatch, hashError, hash, data]);

  const result = useMemo(
    () => ({
      ...requestState,
      // overwrite requestState data with just the data related to the current hash
      data,
      hasError: !!hash && hashError === hash,
    }),
    [data, hash, hashError, requestState],
  );

  if (!hash) {
    return emptyUnnamed;
  }

  return result;
};

export const useSavedRetentionMetricsActions = () => {
  const dispatch = useDispatch<AppDispatch>();
  const notifications = useNotifications();
  const orgId = useSelector(getOrgId);

  // used by both create and update
  const setRetentionMetric = useCallback(
    async (
      request: Omit<SetRetentionMetricRequest, 'orgId'>,
      successMessage: string,
      errorMessage: string,
    ) => {
      try {
        const response = await dispatch(
          RetentionMetricsRequestActions.setRetentionMetric({
            orgId,
            ...request,
          }),
        );
        notifications?.success(successMessage);
        if (request.visibility === Visibility.PUBLIC) {
          dispatch(SegmentRequestActions.getAllSegments(orgId, true));
        }
        return response;
      } catch (e) {
        notifications?.error(errorMessage);
        throw e;
      }
    },
    [dispatch, notifications, orgId],
  );

  const create = useCallback(
    async ({
      request,
      successMessage = intl.formatMessage({
        defaultMessage: 'Successfully created Retention',
        id: 'l6sRzW',
      }),
      errorMessage = intl.formatMessage({
        defaultMessage: 'Failed to create Retention',
        id: 'TlNaBv',
      }),
    }: {
      request: Omit<SetRetentionMetricRequest, 'orgId'>;
      successMessage?: string;
      errorMessage?: string;
    }) => {
      const requestWithId = {
        retentionMetric: {
          id: '',
          name:
            request.retentionMetric?.name ??
            intl.formatMessage({ defaultMessage: 'Untitled Retention', id: 'YpQcSJ' }),
          description: request.retentionMetric?.description ?? '',
          retentionMetric: request.retentionMetric?.retentionMetric ?? null,
          retentionSettings: request.retentionMetric?.retentionSettings ?? null,
        },
        visibility: request.visibility,
      };
      return setRetentionMetric(requestWithId, successMessage, errorMessage);
    },
    [setRetentionMetric],
  );

  const update = useCallback(
    async ({
      request,
      successMessage = intl.formatMessage({
        defaultMessage: 'Successfully created Retention',
        id: 'l6sRzW',
      }),
      errorMessage = intl.formatMessage({
        defaultMessage: 'Failed to create Retention',
        id: 'TlNaBv',
      }),
    }: {
      request: Omit<SetRetentionMetricRequest, 'orgId'>;
      successMessage?: string;
      errorMessage?: string;
    }) => setRetentionMetric(request, successMessage, errorMessage),
    [setRetentionMetric],
  );

  const markViewed = useCallback(
    (retentionMetricId: string) =>
      dispatch(
        RetentionMetricsRequestActions.markRetentionMetricViewed({
          orgId,
          retentionMetricId,
        }),
      ),
    [dispatch, orgId],
  );

  const star = useCallback(
    (retentionMetricId: string) =>
      dispatch(
        RetentionMetricsRequestActions.starRetentionMetric({
          orgId,
          retentionMetricId,
          starred: true,
        }),
      ).catch(error => {
        notifications.error(
          intl.formatMessage({ defaultMessage: 'Failed to star Retention', id: '/X2NEW' }),
        );
        throw error;
      }),
    [dispatch, notifications, orgId],
  );

  const unstar = useCallback(
    (retentionMetricId: string) =>
      dispatch(
        RetentionMetricsRequestActions.starRetentionMetric({
          orgId,
          retentionMetricId,
          starred: false,
        }),
      ).catch(error => {
        notifications.error(
          intl.formatMessage({ defaultMessage: 'Failed to unstar Retention', id: 'hMCGAZ' }),
        );
        throw error;
      }),
    [dispatch, notifications, orgId],
  );

  const createUnnamedRetentionMetric = useCallback(
    (unnamedRetentionMetric: UnnamedRetentionMetric) => {
      const hash = `${hashObject(unnamedRetentionMetric)}`;
      dispatch(
        RetentionMetricsRequestActions.createUnnamedRetentionMetric({
          hash,
          unnamedRetentionMetric,
        }),
      ).catch(error => {
        console.error('Failed to save temporary retention state');
        throw error;
      });
      return hash;
    },
    [dispatch],
  );

  const duplicate = useCallback(
    async (retentionMetric: Omit<SetRetentionMetricRequest, 'orgId'>, space?: Space) => {
      try {
        if (!retentionMetric?.retentionMetric)
          throw new Error('Invalid Retention - no retention provided');
        const response = await dispatch(
          RetentionMetricsRequestActions.setRetentionMetric({
            ...retentionMetric,
            orgId,
            retentionMetric: {
              ...retentionMetric.retentionMetric,
              id: '',
              name: intl.formatMessage(
                { defaultMessage: '{name} (copy)', id: 'oTX17O' },
                { name: retentionMetric.retentionMetric.name },
              ),
            },
          }),
        );
        if (!response.retentionMetric?.retentionMetric) throw new Error('Invalid Retention');
        notifications?.success(
          intl.formatMessage({ defaultMessage: 'Successfully duplicated Retention', id: 'weyJvE' }),
        );

        return response;
      } catch (error) {
        notifications?.error(
          intl.formatMessage({ defaultMessage: 'Failed to duplicate Retention', id: 'ZQQHxx' }),
        );
        throw error;
      }
    },
    [dispatch, orgId, notifications],
  );

  const archive = useCallback(
    async (retentionMetricId: string) => {
      try {
        const response = await dispatch(
          RetentionMetricsRequestActions.archiveRetentionMetric({
            orgId,
            retentionMetricId,
            archived: true,
          }),
        );
        notifications?.success(
          intl.formatMessage({ defaultMessage: 'Successfully archived Retention.', id: 'K9I6Bo' }),
        );
        return response;
      } catch (error) {
        notifications?.error(
          intl.formatMessage({ defaultMessage: 'Failed to archive Retention.', id: 'YPIjdU' }),
        );
        throw error;
      }
    },
    [dispatch, orgId, notifications],
  );

  const restore = useCallback(
    (retentionMetricId: string) => {
      const unarchivedRetentionMetricUrl = generateRoutePath(RETENTION_DETAILS_ROUTE, {
        orgId,
        id: retentionMetricId,
      });
      return dispatch(
        RetentionMetricsRequestActions.archiveRetentionMetric({
          orgId,
          retentionMetricId,
          archived: false,
        }),
      )
        .then(() =>
          notifications?.successLink(
            intl.formatMessage({
              defaultMessage: 'Successfully restored Retention. ',
              id: 's8A0oE',
            }),
            intl.formatMessage({ defaultMessage: 'See it now.', id: 'gzvnO9' }),
            unarchivedRetentionMetricUrl,
          ),
        )
        .catch(error => {
          notifications?.error(
            intl.formatMessage({ defaultMessage: 'Failed to restore Retention.', id: 'H9Q7qa' }),
          );
          throw error;
        });
    },
    [orgId, dispatch, notifications],
  );

  return {
    create,
    update,
    markViewed,
    star,
    unstar,
    createUnnamedRetentionMetric,
    duplicate,
    archive,
    restore,
  };
};

const access = makeUseAccess(RetentionMetricsRequestActions.getAccess, getRetentionMetricsAccess);

const createAccess = makeUseCreateAccess(
  RetentionMetricsRequestActions.getAccess,
  getRetentionMetricsAccess,
);

export const useRetentionAccess = (retentionId: string | null | undefined) => {
  const flags = useFlags();
  const orgId = useSelector(getOrgId);
  const stableIds = useMemo(
    () => (retentionId ? stringIDs([retentionId]) : stringIDs([])),
    [retentionId],
  );

  const requestState = access(stableIds, orgId, flags);
  const canCreate = createAccess(orgId, flags);

  if (retentionId) {
    return {
      access: requestState?.data?.access?.[retentionId],
      accessError: requestState?.error,
      canCreate,
    };
  }

  return {
    access: null,
    accessError: false,
    canCreate,
  };
};
